import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  FormGroupDirective,
  Validators,
  FormArray,
  FormControl,
} from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BehaviorSubject, forkJoin, Subscriber } from 'rxjs';
import { IssuesService } from 'src/app/_services/issues.service';
import { SignedOffService } from 'src/app/_services/signed-off.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { MessageService } from 'primeng/api';
import { formatDate } from '@angular/common';
import { TechnicalViewService } from 'src/app/_services/technical-view.service';
import { VehiclesService } from 'src/app/_services/vehicles.service';

@Component({
  selector: 'app-signoff-issue',
  templateUrl: './signoff-issue.component.html',
  styleUrls: ['./signoff-issue.component.scss'],
})
export class SignoffIssueComponent implements OnInit {
  signOfTypes: any[] = [];
  issueData: any = '';
  signedOffData: any;
  vehicleData: any;
  updateData: any;
  @Output() signOffOutput = new EventEmitter<any>();
  activeIssue: any;
  editFormGroups: FormGroup[] = [];
  selectedSignOffType: any;
  date: string = formatDate(new Date(), 'yyyy-MM-dd', 'en');
  fine_amount: any = '0';
  loading: boolean = false;
  signingOff: boolean = true;
  isMultipleJobs: boolean = false;
  chassis_no: any;
  record_types: any[] = [
    { name: 'Brakes', value: 'brakes' },
    { name: 'Service', value: 'service' },
    { name: 'Tyres', value: 'tyres' },
    { name: 'Maintenance', value: 'maintenance' },
  ];
  selectedRecordType: any;
  selectedIssue: any;

  // The dropdown/template *ngIf sections use lowercase keys, but the
  // established record_type convention already in the database is
  // capitalized and singular for tyres ("Brakes"/"Service"/"Tyre"/
  // "Maintenance") - map to that before sending a new technical record.
  private readonly recordTypeToApi: Record<string, string> = {
    brakes: 'Brakes',
    service: 'Service',
    tyres: 'Tyre',
    maintenance: 'Maintenance',
  };

  remarkKeywords = [
    { label: 'Clean', value: 'Clean' },
    { label: 'Replace', value: 'Replace' },
    { label: 'Repair', value: 'Repair' },
  ];

  @ViewChild(FormGroupDirective)
  private formDir!: FormGroupDirective;

  loadingSignOff$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true
  );

  technicalForm = new FormGroup({
    vehicle_reference: new FormControl(''),
    vehicle_id: new FormControl(''),
    chassis_no: new FormControl(''),
    odometer: new FormControl(''),
    service_jobno: new FormControl(''),
    service_brakes: new FormControl(''),
    remarks: new FormControl(''),
    record_type: new FormControl(''),
    date_completed: new FormControl(''),
    service_type: new FormControl(''),
    tyre_pos: new FormControl(''),
    tyre_size: new FormControl(''),
    tyre_type: new FormControl(''),
    technical_record_jobs: new FormArray([]),
  });

  singleJobForm = this.fb.group({
    id: new FormControl(''),
    description: new FormControl(''),

    remark_keyword: new FormControl(''),
    remarks: new FormControl(''),

    vehicle_reference: new FormControl(''),
    vehicle_id: new FormControl(''),
  });

  constructor(
    private fb: FormBuilder,
    private config: DynamicDialogConfig,
    private issuesService: IssuesService,
    public ref: DynamicDialogRef,
    public signedOffService: SignedOffService,
    private router: Router,
    private location: Location,
    public messageService: MessageService,
    private technicalViewService: TechnicalViewService,
    private vehiclesService: VehiclesService
  ) {}

  ngOnInit(): void {
    this.issueData = this.config.data.descriptionData;
    this.vehicleData = this.config.data.data;
    this.selectedIssue = this.config.data.descriptionData;

    this.signedOffService.signOffForm.reset({
      signoff_type: null,
      fine_amount: null,
      signed_off: null,
      vehicle_reference: null,
      description: null,
      remark_keyword: null,
      remarks: null,
      date_signed_off: null,
      vehicle_id: null,
    });

    if (
      Array.isArray(this.issueData) &&
      Array.isArray(this.vehicleData) &&
      this.vehicleData.length > 0
    ) {
      this.vehicleData.forEach((desc: any) => {
        const formGroup = this.fb.group({
          id: desc.id,
          signoff_type: desc.signoff_type,
          fine_amount: [desc.fine_amount, Validators.required],
          description: this.displayJobDescription(desc.description),

          remark_keyword: '',
          remarks: desc.remarks ?? '',

          vehicle_reference: desc.vehicle_reference,
          date_signed_off: desc.date_signed_off,
          vehicle_id: desc.vehicle_id,
        });
        this.editFormGroups.push(formGroup);
      });
    } else if (this.selectedIssue && !Array.isArray(this.selectedIssue)) {
      const description = this.displayJobDescription(
        this.selectedIssue.description
      );
      const remarks = this.buildRemark(this.selectedIssue.remark_keyword, this.selectedIssue.remarks);
      const vehicleReference =
        this.selectedIssue.vehicle_reference ??
        this.vehicleData?.vehicle_reference ??
        '';
      const vehicleId =
        this.selectedIssue.vehicle_id ??
        this.vehicleData?.vehicle_id ??
        this.vehicleData?.id ??
        '';

      this.singleJobForm.patchValue({
        id: this.selectedIssue.id ?? '',
        description,
        remarks,
        vehicle_reference: vehicleReference,
        vehicle_id: vehicleId,
      });

      this.signedOffService.signOffForm.patchValue({
        description,
        remarks,
        vehicle_reference: vehicleReference,
        vehicle_id: vehicleId,
      });
    }

    const issueCount = Array.isArray(this.issueData)
      ? this.issueData.length
      : 1;
    this.showFineAmount = new Array(issueCount).fill(false);

    this.signOfTypes = [
      { signoff_type: 'General' },
      { signoff_type: 'Technical' },
      { signoff_type: 'Fines' },
    ];

    this.technicalForm.patchValue({
      vehicle_reference: '',
      vehicle_id: '',
      chassis_no: '',
      odometer: '',
      service_jobno: '',
      service_brakes: '',
      remarks: '',
      record_type: '',
      date_completed: '',
      service_type: '',
      tyre_pos: '',
      tyre_size: '',
      tyre_type: '',
      technical_record_jobs: [],
    });

    // Simulate loading completion
    setTimeout(() => {
      this.loadingSignOff$.next(false);
    }, 1000); // Adjust the simulated loading time as needed
  }

  onChange($event: any) {
    this.selectedRecordType = $event.value;
    // console.log(this.selectedRecordType);

    // Reset form values based on selected record type
    this.technicalForm.patchValue({
      service_brakes: '',
      remarks: '',
      service_type: '',
      tyre_pos: '',
      tyre_size: '',
      tyre_type: '',
    });

    // Reset form array
    this.technicalJobArray.clear();
  }

  showFineAmount: boolean[] = new Array(this.issueData.length).fill(false);

  ngOnDestroy(): void {
    this.formDir.resetForm();
  }

  toggleDropdown(event: any): void {
    event.value === 'Technical'
      ? (this.signingOff = true)
      : (this.signingOff = false);
    // console.log('signingOff', this.signingOff);
  }

  toggleFineAmount(event: any, index: number): void {
    const selectedSignoffType = event.value;
    this.showFineAmount[index] = selectedSignoffType === 'Fines';
  }

  get technicalJobArray() {
    return this.technicalForm.get('technical_record_jobs') as FormArray;
  }

  cleanFormData(formData: any): any {
    if (Array.isArray(formData)) {
      // Clean each element in the array and filter out empty objects
      return formData
        .map((item) => this.cleanFormData(item))
        .filter((item) => Object.keys(item).length > 0);
    } else if (typeof formData === 'object' && formData !== null) {
      const cleanedData: any = {};
      for (const key in formData) {
        if (formData.hasOwnProperty(key)) {
          const value = formData[key];
          if (value !== '' && value !== null) {
            if (typeof value === 'object') {
              // Recursively clean nested objects or arrays
              const cleanedValue = this.cleanFormData(value);
              if (
                Object.keys(cleanedValue).length > 0 ||
                Array.isArray(value)
              ) {
                cleanedData[key] = cleanedValue;
              }
            } else {
              cleanedData[key] = value;
            }
          }
        }
      }
      return cleanedData;
    }
    return formData; // Return primitives as-is
  }

  private groupJobsByVehicle(): Map<
    number,
    { vehicle: any; jobs: FormGroup[] }
  > {
    const groupedJobs = new Map<number, { vehicle: any; jobs: FormGroup[] }>();

    // Validate that input data is available
    if (
      !this.editFormGroups ||
      !Array.isArray(this.vehicleData) ||
      this.editFormGroups.length !== this.vehicleData.length
    ) {
      console.error('editFormGroups or vehicleData is not properly populated');
      return groupedJobs;
    }

    // Group jobs by vehicle
    this.editFormGroups.forEach((formGroup, index) => {
      const vehicle = this.vehicleData[index];
      const vehicleId = vehicle.vehicle_id;

      if (!groupedJobs.has(vehicleId)) {
        groupedJobs.set(vehicleId, { vehicle, jobs: [] });
      }

      groupedJobs.get(vehicleId)?.jobs.push(
        this.fb.group({
          id: formGroup.value.id,
          job_description: this.displayJobDescription(
            formGroup.value.description
          ),
          remark_keyword: formGroup.value.remark_keyword,
          remarks: formGroup.value.remarks,
        })
      );
    });

    return groupedJobs;
  }

  private submitSingleSignOff(): void {
    const singleJob = this.singleJobForm.value;
  
    const jobDescription =
      this.displayJobDescription(singleJob.description) ||
      this.displayJobDescription(this.selectedIssue.description);
  
    const jobRemarks = this.buildRemark(singleJob.remark_keyword, singleJob.remarks)
  
    const payload = {
      groups: [
        {
          vehicle_id:
            singleJob.vehicle_id ||
            this.selectedIssue.vehicle_id ||
            this.vehicleData.vehicle_id,
  
          vehicle_reference:
            singleJob.vehicle_reference ||
            this.selectedIssue.vehicle_reference ||
            this.vehicleData.vehicle_reference,
  
          issue_ids: [this.selectedIssue.id],
  
          remarks: [
            {
              job_id: this.selectedIssue.id,
              remarks: jobRemarks,
            },
          ],
  
          technical_record:
            this.selectedSignOffType === 'Technical'
              ? this.cleanFormData({
                  ...this.technicalForm.value,
                  record_type:
                    this.recordTypeToApi[this.technicalForm.value.record_type as string] ??
                    this.technicalForm.value.record_type,
                  vehicle_id:
                    singleJob.vehicle_id ||
                    this.selectedIssue.vehicle_id ||
                    this.vehicleData.vehicle_id,
                  vehicle_reference:
                    singleJob.vehicle_reference ||
                    this.selectedIssue.vehicle_reference ||
                    this.vehicleData.vehicle_reference,
                  technical_record_jobs: [
                    {
                      job_description: jobDescription,
                      remarks: jobRemarks,
                    },
                  ],
                })
              : null,
        },
      ],
  
      signed_off: true,
      signoff_type: this.selectedSignOffType,
      fine_amount:
        this.selectedSignOffType === 'Fines'
          ? this.fine_amount
          : null,
      date_signed_off:
        this.signedOffService.signOffForm.value.date_signed_off ||
        this.date,
    };
  
    console.log('single payload', payload);
  
    this.issuesService.signOffActiveIssue(payload).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Issue has been signed off',
          key: 'active_issues',
        });
  
        this.loading = false;
        this.ref.close(response);
      },
      error: (error) => {
        console.error('Error signing off issue:', error);
  
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to sign off issue',
          key: 'active_issues',
        });
  
        this.loading = false;
      },
    });
  }

  private submitGroupedSignOffs(): void {
    const groupedJobs = this.groupJobsByVehicle();

    const groups = Array.from(groupedJobs.values()).map(({ vehicle, jobs }) => {
      const issueIds = jobs
        .map((job) => job.value?.id)
        .filter((id) => id !== null && id !== undefined && id !== '');

      const remarks = jobs.map((job) => {
        const item = job.value ?? job;

        return {
          job_id: item.id,
          remarks: this.buildRemark(item.remark_keyword, item.remarks),
        };
      });

      const technicalJobs = jobs.map((job) => {
        const item = job.value ?? job;

        return {
          job_description: item.job_description ?? item.description ?? '',
          remarks: this.buildRemark(item.remark_keyword, item.remarks),
        };
      });

      return {
        vehicle_id: vehicle.vehicle_id,
        vehicle_reference: vehicle.vehicle_reference,
        issue_ids: issueIds,
        remarks,

        technical_record:
          this.selectedSignOffType === 'Technical'
            ? this.cleanFormData({
                ...this.technicalForm.value,
                record_type:
                  this.recordTypeToApi[this.technicalForm.value.record_type as string] ??
                  this.technicalForm.value.record_type,
                vehicle_id: vehicle.vehicle_id,
                vehicle_reference: vehicle.vehicle_reference,
                technical_record_jobs: technicalJobs,
              })
            : null,
      };
    });

    const payload = {
      groups,

      signed_off: true,
      signoff_type: this.selectedSignOffType,
      fine_amount:
        this.selectedSignOffType === 'Fines' ? this.fine_amount : null,
      date_signed_off:
        this.signedOffService.signOffForm.value.date_signed_off || this.date,
    };

    console.log('bulk payload', payload);

    if (!groups.length) {
      this.loading = false;
      return;
    }

    this.issuesService.signOffActiveIssue(payload).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Issues signed off successfully',
          key: 'active_issues',
        });

        this.loading = false;
        this.ref.close(response);
      },
      error: (error) => {
        console.error('Failed to sign off grouped issues:', error);

        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to sign off one or more issues',
          key: 'active_issues',
        });

        this.loading = false;
      },
    });
  }

  private buildRemark(keyword?: string | null, extraInfo?: string | null): string {
    const action = (keyword ?? '').trim();
    const info = (extraInfo ?? '').trim();

    if (action && info) {
      return `${action} - ${info}`;
    }

    if (action) {
      return action;
    }

    return info;
  }

  onSignOffIssueFormSubmit(): void {
    if (this.signedOffService.signOffForm.invalid) {
      this.signedOffService.signOffForm.markAllAsTouched();
      return;
    }

    if (
      this.selectedSignOffType === 'Technical' &&
      this.technicalForm.invalid
    ) {
      this.technicalForm.markAllAsTouched();
      return;
    }

    this.loading = true;

    if (this.editFormGroups.length > 0) {
      this.submitGroupedSignOffs();
    } else {
      this.submitSingleSignOff();
    }
  }

  displayJobDescription(description: any): string {
    if (Array.isArray(description)) {
      return description
        .map((item) => this.displayJobDescription(item))
        .filter((item) => item.length > 0)
        .join(', ');
    }

    if (description && typeof description === 'object') {
      return (
        description.value ?? description.label ?? description.description ?? ''
      );
    }

    return description ?? '';
  }
}
